import { memo, ReactNode } from "react";
import { useGoogleLogin } from "@react-oauth/google";

import { useDispatch } from "react-redux";
import { updateUser } from "@/lib/features/authSlice";
import { setCookieSSR } from "@/utils/auth";
import authApiService from "@/services/main/UserServices";
import { ACCESS_TOKEN, ROLE, USER } from "@/constants/common";
import { useRouter, useSearchParams } from "next/navigation";
import { SocialProvider } from "@/models/general";
import image from "@/constants/image";
import Button from "@/components/common/buttons/Button";
import { Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { setErrorMess } from "@/lib/features/statusSlice";
import { useTranslations } from "next-intl";
import Image from "next/image";

const Google = memo(function Goolgle({ children }: { children: ReactNode }) {
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect");
  const dispatch = useDispatch();
  const t = useTranslations("messages");
  const router = useRouter();
  const queryClient = useQueryClient();
  const onSuccessLogin = async (token: string, provider: SocialProvider) => {
    try {
      const response = await authApiService.userSocialLogin({
        token,
        provider,
      });
      dispatch(updateUser(response?.user));
      setCookieSSR(ACCESS_TOKEN, response?.token);
      setCookieSSR(ROLE, response?.user.adminType);
      setCookieSSR(USER, {
        id: response?.user?.id,
        name:
          response?.user?.fullName ??
          `${response?.user?.firstName} ${response?.user?.lastName}`,
        email: response?.user?.email,
      });
      queryClient.clear();
      if (redirect) {
        window.location.href = decodeURIComponent(redirect);
        return;
      } else if (response?.user) {
        window.location.reload();
        return;
      }
    } catch (error: any) {
      dispatch(setErrorMess({ message: error?.detail }));
    }
  };

  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      onSuccessLogin(tokenResponse.access_token, SocialProvider.GOOGLE);
    },
    onError: () => {
      dispatch(setErrorMess({ message: t("auth_login_again") }));
    },
  });

  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={() => {
        login();
      }}
      startIcon={
        <Image src={image.icGoogle} alt="google" width={16} height={16} />
      }
      sx={{
        border: "1px solid rgba(28, 28, 28, 0.2)",
        color: "#333",
        padding: "8px 16px",
        textTransform: "none",
        fontFamily: "Montserrat",
        display: "flex",
        justifyContent: "center",
        gap: "8px",
        borderRadius: "4px",
        lineHeight: "24px",
        letterSpacing: "0.25px",
      }}
    >
      <Typography
        sx={{
          fontStyle: "normal",
          fontSize: "14px",
          color: "var(--eerie-black)",
          fontWeight: 500,
          lineHeight: "140%",
        }}
      >
        {children}
      </Typography>
    </Button>
  );
});

export default Google;
